import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import ToggleArchiveQuestionConfigModal from 'modules/reviews/components/Questions/ArchiveQuestionConfigModal'
import type QuestionConfig from 'modules/reviews/store/models/QuestionConfig'
import type Review from 'modules/reviews/store/models/v2/Review'

type ToggleData = {
  id: number
  surveys_form_id: number
  archived: boolean
}

interface Props {
  form: Review
  questionConfig: QuestionConfig
  toggleArchivedQuestionConfig: (data: ToggleData) => Promise<unknown>
  setOpenedModal: (state: ModalStates) => void
}

export default function Actions({
  form,
  questionConfig,
  toggleArchivedQuestionConfig,
  setOpenedModal,
}: Props) {
  const updateAction = {
    onClick: () => setOpenedModal('edit'),
    title: i18n.t('surveys.form.question_configs.update'),
  }

  const [showToggleArchivedModal, setShowToggleArchivedModal] = useState(false)
  const onCloseToggleArchivedModal = () => {
    setShowToggleArchivedModal(false)
  }
  const questionArchived = questionConfig.attributes.archived

  const archiveAction = {
    onClick: async () => {
      if (form.isActive) {
        setShowToggleArchivedModal(true)
      } else {
        await asyncAction(() => {
          const data = {
            id: questionConfig.attributes.id,
            surveys_form_id: form.id,
            archived: !questionConfig.attributes.archived,
          }

          return toggleArchivedQuestionConfig(data)
        })
      }
    },
    title: questionConfig.attributes.archived
      ? i18n.t('surveys.form.question_configs.convert_to_undraft')
      : i18n.t('surveys.form.question_configs.convert_to_draft'),
  }

  const deleteAction = {
    onClick: () => setOpenedModal('delete'),
    title: i18n.t('surveys.form.question_configs.delete'),
    danger: true,
  }

  const viewAction = {
    onClick: () => setOpenedModal('view'),
    title: i18n.t('surveys.form.question_configs.view'),
  }

  const duplicateAction = {
    onClick: () => setOpenedModal('duplicate'),
    title: i18n.t('surveys.form.question_configs.duplicate.title'),
  }

  const actions = (() => {
    if (questionConfig.attributes.archived || form.isActive) {
      return [archiveAction, viewAction]
    }

    if (form.isFinished) {
      return [archiveAction, viewAction]
    }

    return [updateAction, duplicateAction, archiveAction, deleteAction]
  })()

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary" iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="center">
          {actions.map((action) => (
            <PopoverMenu.TouchableOption key={action.title} {...action} />
          ))}
        </PopoverMenu.Content>
      </PopoverMenu>

      {showToggleArchivedModal && (
        <ToggleArchiveQuestionConfigModal
          formId={form.id}
          onClose={onCloseToggleArchivedModal}
          questionConfigId={questionConfig.id}
          archiving={!questionArchived}
        />
      )}
    </>
  )
}
